import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import BlogCard from "../components/BlogCard";
import BlogPageShell from "../components/BlogPageShell";
import { useLanguage } from "../context/LanguageContext";
import { useSanityQuery } from "../hooks/useSanityQuery";
import { sanityClient, isSanityConfigured } from "../lib/sanityClient";
import { postsQuery } from "../lib/queries";

const Blog = () => {
  const { language, t } = useLanguage();

  const { data: posts, loading, error } = useSanityQuery(
    () => {
      if (!isSanityConfigured) return Promise.resolve([]);
      return sanityClient.fetch(postsQuery, { language });
    },
    [language]
  );

  return (
    <BlogPageShell>
      <div className="pt-28 pb-20">
        <Helmet>
          <title>
            {t("blog.pageTitle")} | {t("app.title")}
          </title>
          <meta name="description" content={t("blog.metaDescription")} />
        </Helmet>

        <div className="container mx-auto px-6">
          <motion.header
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="relative mx-auto mb-16 max-w-3xl text-center"
          >
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-yellow-200/80 bg-white/70 px-4 py-1.5 text-sm font-medium text-yellow-800 shadow-sm backdrop-blur-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-yellow-500" />
              {t("blog.pageTitle")}
            </div>

            <h1 className="mb-5 text-5xl font-extrabold tracking-tight text-gray-900 md:text-6xl">
              <span className="bg-gradient-to-r from-gray-900 via-gray-800 to-yellow-700 bg-clip-text text-transparent">
                {t("blog.heroTitle")}
              </span>
            </h1>

            <p className="mx-auto max-w-2xl text-lg leading-relaxed text-gray-600">
              {t("blog.heroSubtitle")}
            </p>

            <div className="mx-auto mt-8 flex items-center justify-center gap-3">
              <span className="h-px w-16 bg-gradient-to-r from-transparent to-yellow-400" />
              <span className="h-2 w-2 rotate-45 border border-yellow-400 bg-yellow-100" />
              <span className="h-px w-16 bg-gradient-to-l from-transparent to-yellow-400" />
            </div>
          </motion.header>

          {!isSanityConfigured && (
            <div className="mx-auto max-w-xl rounded-2xl border border-yellow-100/80 bg-white/80 p-12 text-center shadow-[0_8px_40px_rgba(161,98,7,0.08)] backdrop-blur-sm">
              <p className="text-lg text-gray-600">{t("blog.notConfigured")}</p>
            </div>
          )}

          {isSanityConfigured && loading && (
            <div className="py-20 text-center">
              <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-yellow-200 border-t-yellow-500" />
              <p className="mt-4 font-medium text-gray-500">{t("blog.loading")}</p>
            </div>
          )}

          {isSanityConfigured && error && (
            <div className="mx-auto max-w-xl rounded-2xl border border-red-200 bg-red-50/80 p-12 text-center backdrop-blur-sm">
              <p className="text-red-600">{t("blog.errorLoading")}</p>
            </div>
          )}

          {isSanityConfigured && !loading && !error && (!posts || posts.length === 0) && (
            <div className="mx-auto max-w-xl rounded-2xl border border-yellow-100/80 bg-white/80 p-12 text-center shadow-[0_8px_40px_rgba(161,98,7,0.08)] backdrop-blur-sm">
              <p className="text-lg text-gray-600">{t("blog.noPosts")}</p>
            </div>
          )}

          {isSanityConfigured && !loading && !error && posts?.length > 0 && (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                visible: { transition: { staggerChildren: 0.12 } },
              }}
              className="mx-auto grid max-w-6xl grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3"
            >
              {posts.map((post) => (
                <motion.div
                  key={post._id}
                  variants={{
                    hidden: { opacity: 0, y: 32 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
                  }}
                >
                  <BlogCard post={post} />
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </BlogPageShell>
  );
};

export default Blog;
